import React from 'react';
import { Link, Text } from 'office-ui-fabric-react';
import {
    TextField,
    IconButton,
    IIconProps,
    IImageProps,
    TooltipHost,
    DefaultButton,
    PrimaryButton,
    Image,
    Icon,
    Checkbox
} from '@fluentui/react';
import InfoIcon from '../../../static/icons/tooltip-info-icon.svg';
import { useId } from '@fluentui/react-hooks';
import { ISelectedData } from 'app/models/common/response';
import NumberFormat from 'react-number-format';
import { keyPress } from 'app/components/helpers';
import './AddMultiDetails.scss';
import { RequestDto, RequestType } from 'generated/request-backend';
import ImpactReasonFields, { isImpactReasonComplete } from '../ImpactReasonFields';
import { useIsNewTicketViewEnabled } from 'app/modules/newTicketView/viewFlag';
import { logFlowEvent } from 'app/modules/user/createTicket/flowTelemetry';
import { TelemetryConstants } from 'app/utils/constants';
import { SAME_ISSUE_DESCRIPTION } from 'app/modules/user/createTicket/customerImpact/impactDescriptionConstants';

const IMPACT_DESCRIPTION_MAX = 2000;
const IMPACT_DESCRIPTION_COUNTER_THRESHOLD = 1600;

const parseNumericValue = (value?: number | string) => {
    const parsed = Number(String(value ?? '').replace(/[$,]/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
};

const exceedsMsxCap = (entry: ISelectedData, field: 'revenue' | 'devices') => {
    if (!entry.opportunityId) {
        return false;
    }

    const entered = parseNumericValue(field === 'revenue' ? entry.estimatedValue : entry.totalDeviceCount);
    const cap = parseNumericValue(field === 'revenue' ? entry.estimatedRevenue : entry.estimatedDevices);
    return cap > 0 && entered > cap;
};

const getAutomationId = (entry: ISelectedData) =>
    (entry.opportunityNumber || entry.accountNumber || entry.opportunityId || entry.accountName || 'customer')
        .replace(/[^a-zA-Z0-9_-]/g, '-');

const getCheckboxInputProps = (automationId: string) => ({
    title: 'Same issue as ticket',
    'data-testid': `same-issue-checkbox-${automationId}`
});

interface IAddDetailsProps {
    isEdit?: boolean;
    retrySynchronization?: boolean;
    selectedRequest: RequestDto;
    setDisplayScreen?: (displayScreen: string) => void;
    onDismiss?: () => void;
    selectedData: Array<ISelectedData>;
    handleMultiChange: (e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, type: string, id: string) => void;
    handleMultiChanges: (id: string, changes: Partial<ISelectedData>) => void;
    multiFormError?: boolean;
    cssErrorText?: boolean;
    handleAddCustomer?: () => void;
    cssNumber?: string;
    setCssNumber?: (cssNumber: string) => void;
    setErrorText?: (cssError: boolean) => void;
}

const AddMultiDetails: React.FC<IAddDetailsProps> = (props) => {
    const {
        selectedRequest,
        onDismiss,
        setDisplayScreen,
        selectedData,
        handleMultiChange,
        handleMultiChanges,
        multiFormError,
        cssNumber,
        setCssNumber,
        handleAddCustomer,
        cssErrorText,
        setErrorText,
        isEdit = false,
        retrySynchronization = false
    } = props;

    const cancelIcon: IIconProps = { iconName: 'Cancel' };
    const calloutProps = { gapSpace: 0 };
    const tooltipId = useId('tooltip');
    const isNewTicketViewEnabled = useIsNewTicketViewEnabled();

    const clickHandler = () => {
        if (isEdit) { onDismiss(); return; }
        if (isNewTicketViewEnabled) {
            logFlowEvent(TelemetryConstants.CUSTOMER_IMPACT_PANEL_CANCELLED, 'Add Customer', {
                customerCount: selectedData.length
            });
        }
        setDisplayScreen('searchCust');
    };

    const imageProps: IImageProps = {
        styles: () => ({ root: { paddingBottom: '10px' } }),
    };

    const fireChange = (item: ISelectedData, type: string, value: string) =>
        handleMultiChange(
            { target: { value } } as unknown as React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
            type,
            item.opportunityNumber
        );

    const handleSameIssue = (item: ISelectedData, checked: boolean) => {
        handleMultiChanges(item.opportunityNumber, {
            sameIssue: checked,
            customerImpact: checked ? (isEdit ? SAME_ISSUE_DESCRIPTION : (selectedRequest.summary || '')) : ''
        });
    };

    const handleReasonFieldChange = (item: ISelectedData, type: string, value: string) => {
        if (type !== 'entire_deal') {
            fireChange(item, type, value);
            return;
        }

        const isChecked = value === 'true';
        handleMultiChanges(item.opportunityNumber, {
            entireDealDepends: isChecked,
            estimatedValue: isChecked ? String(item.estimatedRevenue ?? '') : '',
            totalDeviceCount: isChecked ? String(item.estimatedDevices ?? '') : '',
            revenueErrorText: false,
            devicesErrorText: false
        });
    };

    const handleImpactValueChange = (
        event: React.FormEvent<HTMLInputElement>,
        item: ISelectedData,
        field: 'impacted_revenue' | 'impacted_device'
    ) => {
        handleMultiChange(event, field, item.opportunityNumber);
        const enteredAmount = parseNumericValue(event.currentTarget.value);
        const msxEstimate = parseNumericValue(
            field === 'impacted_revenue' ? item.estimatedRevenue : item.estimatedDevices
        );
        if (!isEdit && item.opportunityId && msxEstimate > 0 && enteredAmount > msxEstimate) {
            logFlowEvent(TelemetryConstants.CUSTOMER_MSX_CAP_WARNING, 'Add Customer', {
                field: field === 'impacted_revenue' ? 'revenueAtRisk' : 'devicesAffected',
                enteredAmount,
                msxEstimate
            });
        }
    };

    const submitHandler = () => {
        if (
            (selectedRequest.requestType === RequestType.CssReviewRequest ||
                (isEdit && selectedRequest.requestType === RequestType.CSSRequest) ||
                selectedRequest.requestType === RequestType.CXP_TechnicalRequest) &&
            !cssNumber
        ) {
            setErrorText(true);
            return;
        }
        logFlowEvent(TelemetryConstants.CUSTOMER_IMPACT_PANEL_COMPLETED, 'Add Customer', {
            customerCount: selectedData.length
        });
        handleAddCustomer();
    };

    if (isNewTicketViewEnabled) {
        const isIncomplete = selectedData.some(
            (entry) =>
                !isImpactReasonComplete(entry) ||
                !entry.customerImpact?.trim() ||
                exceedsMsxCap(entry, 'revenue') ||
                exceedsMsxCap(entry, 'devices')
        );

        return (
            <div className="selected-customer-container">
                <div className="close-model">
                    <IconButton
                        ariaLabel="Close popup modal"
                        data-testid="add-customer-close-button"
                        iconProps={cancelIcon}
                        onClick={() => onDismiss()}
                    />
                </div>
                <div className="modal-heading">
                    <span>{isEdit ? 'Edit customer impact' : 'Add customer'}</span>
                    {(selectedRequest.unifiedTrackingId || selectedRequest.summary) && (
                        <div className="drawer-subtitle">
                            {selectedRequest.unifiedTrackingId}
                            {selectedRequest.unifiedTrackingId && selectedRequest.summary ? ' · ' : ''}
                            {selectedRequest.summary}
                        </div>
                    )}
                </div>
                <div className="modal-body">
                    <div className="body-result-container">
                        <div className="opportunity-impact-form">
                            <div className="opportunity-impact-form-body">
                                {(selectedRequest.requestType === RequestType.CssReviewRequest ||
                                    (isEdit && selectedRequest.requestType === RequestType.CSSRequest) ||
                                    selectedRequest.requestType === RequestType.CXP_TechnicalRequest) && (
                                    <div className="info-css-textfield-container">
                                        <div className="css-case-styles">
                                            <div className="tooltip-styles">
                                                <label className="label-styles">CSS Case Number</label>
                                                <TooltipHost
                                                    id={tooltipId}
                                                    calloutProps={calloutProps}
                                                    content="For this type of request please have a CSS support case open first">
                                                    <Icon
                                                        iconName="Info"
                                                        className="field-info-icon"
                                                        aria-describedby={tooltipId}
                                                    />
                                                </TooltipHost>
                                            </div>
                                            <div className="info-container">
                                                <TextField
                                                    value={cssNumber}
                                                    ariaLabel="CSS Case Number"
                                                    data-testid="css-case-number-input"
                                                    onChange={(e, value) => {
                                                        setCssNumber(value || '');
                                                        setErrorText(false);
                                                    }}
                                                />
                                            </div>
                                            {cssErrorText && (
                                                <span className="helper-text">Please enter the CSS Case Number.</span>
                                            )}
                                        </div>
                                    </div>
                                )}
                                {selectedData.map((item: ISelectedData, index) => {
                                    const automationId = getAutomationId(item);
                                    return (
                                        <div
                                            className="impact-customer-section"
                                            key={automationId}>
                                        {selectedData.length > 1 && (
                                            <Text className="impact-customer-heading">
                                                Customer {index + 1} of {selectedData.length}
                                            </Text>
                                        )}
                                        <Text className="impact-section-heading">
                                            {item.opportunityId ? 'Opportunity details' : 'Customer details'}
                                        </Text>
                                        <div className="opportunity-details-section">
                                            <div className="details-grid">
                                                <div className="detail-row">
                                                    <Text className="detail-label">
                                                        {item.opportunityId ? 'Opportunity' : 'Customer name'}
                                                    </Text>
                                                    <Text className="detail-value">
                                                        {item.opportunityId ? item.topic : item.accountName}
                                                    </Text>
                                                </div>
                                                {!item.opportunityId && (
                                                    <div className="detail-row">
                                                        <Text className="detail-label">Account ID</Text>
                                                        <Text className="detail-value">{item.accountNumber || '—'}</Text>
                                                    </div>
                                                )}
                                                <div className="detail-row">
                                                    <Text className="detail-label">Country</Text>
                                                    <Text className="detail-value">{item.country}</Text>
                                                </div>
                                                {item.opportunityId && (
                                                    <>
                                                        <div className="detail-row msx-detail-row">
                                                            <Text className="detail-label">Estimated Revenue</Text>
                                                            <Text className="detail-value">
                                                                ${parseNumericValue(item.estimatedRevenue).toLocaleString()}
                                                            </Text>
                                                        </div>
                                                        <div className="detail-row msx-detail-row">
                                                            <Text className="detail-label">Estimated Devices</Text>
                                                            <Text className="detail-value">
                                                                {parseNumericValue(item.estimatedDevices).toLocaleString()}
                                                            </Text>
                                                        </div>
                                                    </>
                                                )}
                                            </div>
                                        </div>
                                        <Text className="impact-section-heading">Estimate the impact of this issue</Text>
                                        <div className={`impacted-text-fields${item.entireDealDepends ? ' impact-values--entire-deal' : ''}`}>
                                            <div>
                                                <label className="label-styles">
                                                    Revenue at risk<span className="required-star">*</span>
                                                    <TooltipHost content="Estimated revenue at risk in USD">
                                                        <Icon iconName="Info" className="field-info-icon" />
                                                    </TooltipHost>
                                                </label>
                                                <NumberFormat
                                                    aria-label="Revenue at risk"
                                                    aria-required="true"
                                                    aria-invalid={item.revenueErrorText || exceedsMsxCap(item, 'revenue')}
                                                    aria-describedby={
                                                        item.revenueErrorText || exceedsMsxCap(item, 'revenue')
                                                            ? `revenue-error-${automationId}`
                                                            : undefined
                                                    }
                                                    required
                                                    data-testid={`revenue-at-risk-input-${automationId}`}
                                                    thousandSeparator={true}
                                                    prefix="$"
                                                    value={item.estimatedValue}
                                                    onChange={(e) => handleImpactValueChange(e, item, 'impacted_revenue')}
                                                    placeholder="e.g., $500,000"
                                                    allowNegative={true}
                                                    inputMode="numeric"
                                                    className="num-foramt"
                                                />
                                                {(item.revenueErrorText || exceedsMsxCap(item, 'revenue')) && (
                                                    <span className="helper-text" id={`revenue-error-${automationId}`}>
                                                        {exceedsMsxCap(item, 'revenue')
                                                            ? 'Revenue at risk cannot exceed the MSX estimate.'
                                                            : 'Please enter the revenue risk involved.'}
                                                    </span>
                                                )}
                                            </div>
                                            <div>
                                                <label className="label-styles">
                                                    Devices affected<span className="required-star">*</span>
                                                    <TooltipHost content="Number of devices impacted">
                                                        <Icon iconName="Info" className="field-info-icon" />
                                                    </TooltipHost>
                                                </label>
                                                <NumberFormat
                                                    aria-label="Devices affected"
                                                    aria-required="true"
                                                    aria-invalid={item.devicesErrorText || exceedsMsxCap(item, 'devices')}
                                                    aria-describedby={
                                                        item.devicesErrorText || exceedsMsxCap(item, 'devices')
                                                            ? `devices-error-${automationId}`
                                                            : undefined
                                                    }
                                                    required
                                                    data-testid={`devices-affected-input-${automationId}`}
                                                    thousandSeparator={true}
                                                    value={item.totalDeviceCount}
                                                    onChange={(e) => handleImpactValueChange(e, item, 'impacted_device')}
                                                    placeholder="e.g., 200"
                                                    allowNegative={true}
                                                    inputMode="numeric"
                                                    className="num-foramt"
                                                    decimalScale={0}
                                                />
                                                {(item.devicesErrorText || exceedsMsxCap(item, 'devices')) && (
                                                    <span className="helper-text" id={`devices-error-${automationId}`}>
                                                        {exceedsMsxCap(item, 'devices')
                                                            ? 'Devices affected cannot exceed the MSX estimate.'
                                                            : 'Please enter the device risk involved.'}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <ImpactReasonFields
                                            item={item}
                                            showEntireDeal={!!item.opportunityId}
                                            showSameIssue={false}
                                            onFieldChange={(type, value) => handleReasonFieldChange(item, type, value)}
                                        />
                                        <div className="impact-description-field">
                                            <label className="label-styles">
                                                Customer impact description<span className="required-star">*</span>
                                            </label>
                                            <div className="impact-checkbox">
                                                <Checkbox
                                                    label="This customer also has the same issue as the ticket"
                                                    checked={!!item.sameIssue}
                                                    inputProps={getCheckboxInputProps(automationId)}
                                                    onChange={(_e, checked) => handleSameIssue(item, !!checked)}
                                                />
                                            </div>
                                            <TextField
                                                className="cust-impact"
                                                multiline={true}
                                                maxLength={IMPACT_DESCRIPTION_MAX}
                                                ariaLabel="Describe how this issue is impacting this customer"
                                                aria-required={true}
                                                aria-invalid={!item.customerImpact?.trim()}
                                                required
                                                data-testid={`customer-impact-description-${automationId}`}
                                                placeholder="Describe how this issue is impacting this customer specifically..."
                                                value={item.customerImpact}
                                                onChange={(e, value) => {
                                                    if (isEdit) {
                                                        handleMultiChanges(item.opportunityNumber, {
                                                            customerImpact: value ?? '',
                                                            sameIssue: value === SAME_ISSUE_DESCRIPTION
                                                        });
                                                    } else {
                                                        handleMultiChange(e, 'customer_impact', item.opportunityNumber);
                                                    }
                                                }}
                                            />
                                            {(item.customerImpact?.length ?? 0) >= IMPACT_DESCRIPTION_COUNTER_THRESHOLD && (
                                                <div className="char-counter">
                                                    {item.customerImpact?.length ?? 0}/{IMPACT_DESCRIPTION_MAX}
                                                </div>
                                            )}
                                        </div>
                                        </div>
                                    );
                                })}
                            </div>
                            {multiFormError && (
                                <div className="helper-txt" role="alert" aria-live="assertive">
                                    Please provide customer impact details for every selected customer.
                                </div>
                            )}
                            <div className="add-details-btn">
                                <DefaultButton
                                    text={isEdit ? 'Cancel' : 'Back'}
                                    onClick={clickHandler}
                                    data-testid="add-customer-back-button"
                                />
                                <PrimaryButton
                                    text={isEdit ? (retrySynchronization ? 'Retry synchronization' : 'Save changes') : 'Add to ticket'}
                                    onClick={submitHandler}
                                    disabled={isIncomplete}
                                    data-testid="add-customer-submit-button"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="selected-customer-container">
            <div className={`close-model `}>
                <IconButton
                    role="button"
                    aria-label="close popup"
                    iconProps={cancelIcon}
                    ariaLabel="Close popup modal"
                    tabIndex={0}
                    onClick={() => onDismiss()}
                    onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => keyPress(e, () => onDismiss())}
                />
            </div>
            <div className="modal-heading">
                <span>{isEdit ? 'Edit customer impact' : 'Add a customer'}</span>
            </div>
            <div className="modal-body">
                <div className="body-main-container">
                    <Text className="modal-body-title back-title">
                        <Link
                            aria-label="back button"
                            tabIndex={0}
                            onClick={clickHandler}
                            onKeyPress={(e: React.KeyboardEvent<HTMLAnchorElement>) => keyPress(e, clickHandler)}
                            data-testid="search-customer">
                            <span className="arrow-icon"></span>
                        </Link>
                        Add Details
                    </Text>
                    <div className="body-main-sub">
                        <div className="req-app-styles">
                            <Text className="secondary-text mb-10">
                                <span className="text-bold">Request ID - </span>
                                {selectedRequest.unifiedTrackingId}
                            </Text>
                            <div className="secondary-text mb-10 pipe-styles"> </div>
                            <Text className="secondary-text mb-10">
                                <span className="text-bold"> Application ID - </span>
                                {selectedRequest.id}
                            </Text>
                        </div>
                        <div>
                            <Text className="modal-body-title title-field">{selectedRequest.summary}</Text>
                        </div>
                    </div>
                    <div className="body-main-sub">
                        <div className="req-app-styles">
                            <Text className="secondary-text mb-10">
                                <span className="text-bold">Customer Name - </span>
                                {selectedData[0]?.accountName}
                            </Text>
                            <div className="secondary-text mb-10 pipe-styles"> </div>
                            <Text className="secondary-text mb-10">
                                <span className="text-bold"> Account ID - </span>
                                {selectedData[0]?.accountNumber}
                            </Text>
                            <div className="secondary-text mb-10 pipe-styles"> </div>
                            <Text className="secondary-text mb-10">
                                <span className="text-bold"> Country/Subsidiary  - </span>
                                {selectedData[0]?.country}
                            </Text>
                        </div>
                    </div>
                </div>
                <div className="body-result-container">
                    <div>
                        {(selectedRequest.requestType === RequestType.CssReviewRequest ||
                            (isEdit && selectedRequest.requestType === RequestType.CSSRequest) ||
                            selectedRequest.requestType === RequestType.CXP_TechnicalRequest) && (
                            <div className="info-css-textfield-container row-flex-box m-b-25">
                            <div className="css-case-styles">
                                <div className="tooltip-styles">
                                    <label className="label-styles">CSS Case Number</label>
                                    <Text>
                                        <TooltipHost
                                            className="tooltip-display"
                                            id={tooltipId}
                                            calloutProps={calloutProps}
                                            content={
                                                'For this type of request please have a CSS support case open first'
                                            }>
                                            <Image
                                                {...imageProps}
                                                aria-describedby={tooltipId} src={InfoIcon} alt="Info"
                                            />
                                        </TooltipHost>
                                    </Text>
                                </div>
                                <div className="info-container">
                                    <TextField
                                        ariaLabel={isEdit ? 'CSS Case Number' : undefined}
                                        required={isEdit}
                                        value={cssNumber}
                                        onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                            if (!isEdit) handleMultiChange(e, 'css-case-num', (e.target as HTMLInputElement | HTMLTextAreaElement).value);
                                            setCssNumber(isEdit ? (newValue ?? '') : (e.target as HTMLInputElement | HTMLTextAreaElement).value);
                                            setErrorText(false);
                                        }}
                                    />

                                </div>
                                <div>
                                    {cssErrorText &&
                                        <div className="error-info-message">
                                            <span className="helper-txt">Please enter the CSS Case Number.</span>

                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                        )}
                    </div>
                    {selectedData.map((item: ISelectedData) => (
                        <div>
                            <div className="impacted-text-fields">
                                <div>
                                    <label className="label-styles">Impacted Revenue ($)</label>
                                    <NumberFormat
                                        aria-label='Impacted Revenue'
                                        data-testid={`impacted-revenue-${item.opportunityNumber}`}
                                        thousandSeparator={true}
                                        value={item.estimatedValue}
                                        onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                            handleMultiChange(e, 'impacted_revenue', item.opportunityNumber);
                                        }}
                                        placeholder="$"
                                        allowNegative={true}
                                        inputMode="numeric"
                                        className="num-foramt"
                                        decimalScale={2}
                                    />
                                    {item.revenueErrorText && (
                                        <span className="helper-txt">Please enter the revenue risk involved.</span>
                                    )}
                                </div>
                                <div>
                                    <label className="label-styles">Impacted Devices</label>
                                    <NumberFormat
                                        aria-label='Impacted Devices'
                                        data-testid={`impacted-devices-${item.opportunityNumber}`}
                                        thousandSeparator={true}
                                        value={item.totalDeviceCount}
                                        onChange={(e: React.FormEvent<HTMLInputElement>) => {
                                            handleMultiChange(e, 'impacted_device', item.opportunityNumber);
                                        }}
                                        placeholder="0"
                                        allowNegative={true}
                                        inputMode="numeric"
                                        className="num-foramt"
                                        decimalScale={0}
                                    />
                                    {item.devicesErrorText && (
                                        <span className="helper-txt">Please enter the device risk involved.</span>
                                    )}
                                </div>
                            </div>
                            <div>
                                <label className="label-styles">Please tell us how this impacts your customer</label>
                                <TextField
                                    ariaLabel='Enter How this impacts your customer'
                                    className="cust-impact"
                                    multiline={true}
                                    onChange={(e: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
                                        if (isEdit) {
                                            const value = newValue ?? '';
                                            handleMultiChanges(item.opportunityNumber, {
                                                customerImpact: value, sameIssue: value === SAME_ISSUE_DESCRIPTION
                                            });
                                        } else {
                                            handleMultiChange(e, 'customer_impact', item.opportunityNumber);
                                        }
                                    }}
                                    value={item.customerImpact}
                                />
                            </div>
                            {isNewTicketViewEnabled && (
                                <ImpactReasonFields
                                    item={item}
                                    showEntireDeal={!!item.opportunityId}
                                    onFieldChange={(type, value) =>
                                        handleMultiChange(
                                            { target: { value } } as unknown as React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
                                            type,
                                            item.opportunityNumber
                                        )
                                    }
                                />
                            )}
                        </div>
                    ))}
                    <div>
                        {multiFormError && (
                            <div className="helper-txt" role="alert" aria-live="assertive">
                                Please provide customer impact details for every selected customer.
                            </div>
                        )}
                    </div>
                    <div className="add-details-btn">
                        <DefaultButton
                            tabIndex={0}
                            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                                onDismiss();
                            }}
                            onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => keyPress(e, () => onDismiss())}
                            text="Cancel"
                        />
                        <PrimaryButton
                            tabIndex={0}
                            onClick={() => {
                                if (selectedRequest.requestType === RequestType.CssReviewRequest && (cssNumber === '' || cssNumber === undefined)) {
                                    setErrorText(true);
                                    return;
                                }
                                handleAddCustomer();
                            }}
                            onKeyPress={(e: React.KeyboardEvent<HTMLButtonElement>) => keyPress(e, () => handleAddCustomer())}
                            text={isEdit ? 'Save changes' : 'Next'}
                            data-testid="add-customer-next-button"
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddMultiDetails;
